// @ts-nocheck
import React, { useState } from "react";
import * as services from "../Services/services";
import { formatMinutes } from "../utils/lessonMedia";
import { enrollOffer } from "../utils/lmsLearner";
import { ok, message, refusalCode, payload, useLoad, Status } from "./learningShared";

/*
 * View All — every published course and path this person's audience may see (decision 7, Scott
 * 2026-10-16: customers get "Assigned" and "View All"). Enrol buttons appear only while AVS OS's
 * learning.copilot.selfEnrollEnabled is on; with it off the list is still readable and each entry
 * says to ask a training admin. AVS OS enforces the switch either way (403 self_enroll_off).
 */

function EnrollButton({ kind, id, name, onEnrolled }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    const resp = kind === "path" ? await services.lmsEnrollPath(id) : await services.lmsEnrollCourse(id);
    setBusy(false);
    if (!ok(resp)) {
      setError(
        refusalCode(resp) === "self_enroll_off"
          ? "Enrolling yourself isn't switched on. Ask your training admin to add you."
          : message(resp, "That didn't work, please try again.")
      );
      return;
    }
    onEnrolled(payload(resp));
  };
  return (
    <span className="d-inline-flex flex-column align-items-end">
      <button
        type="button"
        className="btn btn-primary btn-sm text-nowrap"
        onClick={run}
        disabled={busy}
        aria-label={`Enroll in ${name}`}
        data-testid={`learning-enroll-${kind}`}
      >
        {busy ? "Enrolling…" : "Enroll"}
      </button>
      {error && (
        <span className="small text-danger mt-1 text-end" role="alert" data-testid="learning-enroll-error">
          {error}
        </span>
      )}
    </span>
  );
}

function Offer({ offer, kind, id, name, onOpen, onEnrolled }) {
  if (offer === "enrolled")
    return (
      <button
        type="button"
        className="btn btn-outline-primary btn-sm text-nowrap"
        onClick={onOpen}
        aria-label={`Open ${name}`}
        data-testid={`learning-catalog-open-${kind}`}
      >
        Open
      </button>
    );
  if (offer === "enroll") return <EnrollButton kind={kind} id={id} name={name} onEnrolled={onEnrolled} />;
  return null;
}

export default function LearningCatalog({ nav, onEnrolled }) {
  const { loading, error, data, reload } = useLoad(() => services.lmsCatalog(), []);
  const [justEnrolled, setJustEnrolled] = useState(null);
  const selfEnroll = Boolean(data?.selfEnrollEnabled);

  const enrolled = (kind, name) => () => {
    setJustEnrolled(`You're enrolled in ${name}. It's now on your Assigned list.`);
    reload();
    onEnrolled?.();
  };

  return (
    <div data-testid="learning-catalog">
      <p className="text-body-secondary mb-3">
        Everything you can take.{" "}
        {selfEnroll ? "Enroll in anything that looks useful." : "To take something that isn't assigned to you, ask your training admin to add you."}
      </p>
      {justEnrolled && (
        <div className="alert alert-success py-2" role="status" data-testid="learning-enrolled">
          {justEnrolled}
        </div>
      )}
      <Status loading={loading} error={error} onRetry={reload} />
      {data && data.paths.length === 0 && data.courses.length === 0 && (
        <p className="text-body-secondary" data-testid="learning-catalog-empty">
          There's no training available to browse yet.
        </p>
      )}
      {data && data.paths.length > 0 && (
        <section className="mb-4" aria-labelledby="learning-catalog-paths">
          <h2 id="learning-catalog-paths" className="h6 text-body-secondary text-uppercase mb-3">
            Learning paths
          </h2>
          <ul className="list-unstyled mb-0">
            {data.paths.map((p) => (
              <li key={p.pathId} className="card mb-3 learning-card learning-path-card" data-testid="learning-catalog-path">
                <div className="card-body d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h3 className="h6 mb-1 learning-title">{p.name}</h3>
                    {p.description && <p className="text-body-secondary mb-1">{p.description}</p>}
                    <div className="small text-body-secondary">
                      {p.courseCount} course{p.courseCount === 1 ? "" : "s"}
                      {p.courses?.length > 0 && <>: {p.courses.map((c) => c.name).join(", ")}</>}
                    </div>
                  </div>
                  <Offer
                    offer={enrollOffer(p, selfEnroll)}
                    kind="path"
                    id={p.pathId}
                    name={p.name}
                    onOpen={() => nav.path(p.pathId)}
                    onEnrolled={enrolled("path", p.name)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {data && data.courses.length > 0 && (
        <section aria-labelledby="learning-catalog-courses">
          <h2 id="learning-catalog-courses" className="h6 text-body-secondary text-uppercase mb-3">
            Courses
          </h2>
          <ul className="list-unstyled mb-0">
            {data.courses.map((c) => (
              <li key={c.courseId} className="card mb-3 learning-card" data-testid="learning-catalog-course">
                <div className="card-body d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h3 className="h6 mb-1 learning-title">{c.name}</h3>
                    {(c.intro || c.description) && <p className="text-body-secondary mb-1">{c.intro || c.description}</p>}
                    <div className="small text-body-secondary">
                      {[`${c.lessons} lesson${c.lessons === 1 ? "" : "s"}`, formatMinutes(c.estimatedMinutes)].filter(Boolean).join(" · ")}
                      {c.enrollmentStatus === "completed" && <span className="badge text-bg-success ms-2">Complete</span>}
                      {c.onPath && !c.enrolled && <span className="ms-2">(on one of your paths)</span>}
                    </div>
                  </div>
                  <Offer
                    offer={enrollOffer(c, selfEnroll)}
                    kind="course"
                    id={c.courseId}
                    name={c.name}
                    onOpen={() => nav.course(c.courseId)}
                    onEnrolled={enrolled("course", c.name)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
